import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { CustomerAccountStore } from '../domain/customer-account.store';
import { BillingParty } from '../domain/signup';
import { BillingPartyFormComponent } from './billing-party-form.component';
import { CancelSubscriptionDialog, CancelSubscriptionDialogData } from './cancel-subscription.dialog';
import { InvoiceListComponent } from './invoice-list.component';
import { OrganizationKeyPromptComponent } from './organization-key-prompt.component';
import { SubscriptionStatusCardComponent } from './subscription-status-card.component';

/**
 * The customer portal's shell: subscription, invoices, editable customer data, and cancelling.
 *
 * **It opens on the account's own applications.** One person is one login, so a customer with two
 * subscriptions has two applications here: with several the page shows "Your applications" cards,
 * and "Manage" points the tabs at one of them. The list comes from the signed-in token; when it is
 * empty or unavailable, the key comes from what was remembered at the end of a sign-up on this
 * browser — and from {@link OrganizationKeyPromptComponent} when nothing was. That is also why the portal is behind
 * `platform-subscription.portal.enabled` and off by default: as shipped, anyone who knows a key can
 * read that tenant's invoices.
 *
 * **A disabled portal and an unknown organization look identical here, deliberately.** Every
 * `/customers/**` path answers 404 when the flag is unset, because the controller is not registered
 * at all — so this renders the key prompt in both cases rather than trying to tell them apart.
 * Telling them apart would publish whether the flag is on.
 *
 * **Success is a snackbar; failure is not handled here.** `centralHttpErrorInterceptor` already
 * surfaces HTTP failures, so a second message would be two things saying the same thing. What no
 * interceptor can know is that a save succeeded, which is the one thing this component announces.
 */
@Component({
  selector: 'pp-customer-home',
  imports: [
    MatCard,
    MatCardTitle,
    MatCardContent,
    MatButton,
    MatProgressSpinner,
    MatTabsModule,
    TranslocoDirective,
    OrganizationKeyPromptComponent,
    SubscriptionStatusCardComponent,
    InvoiceListComponent,
    BillingPartyFormComponent,
  ],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.portal'">
      <h1>{{ t('title') }}</h1>

      @if (store.choosingApplication()) {
        <section class="portal-applications" test-id="portal-applications">
          <h2>{{ t('applications.title') }}</h2>
          <p class="portal-note">{{ t('applications.lead') }}</p>
          @for (application of store.applications(); track application.orgKey) {
            <mat-card class="portal-application" [attr.test-id]="'portal-application-' + application.orgKey">
              <mat-card-title>{{ application.organizationName ?? application.orgKey }}</mat-card-title>
              <mat-card-content>
                <p class="portal-org-key">{{ t('orgKey', { orgKey: application.orgKey }) }}</p>
                @if (application.planName) {
                  <p class="portal-note">{{ application.planName }}</p>
                }
                @if (application.applicationUrl) {
                  <a mat-raised-button color="primary" [href]="application.applicationUrl">{{ t('openApplication') }}</a>
                } @else {
                  <p class="portal-note">{{ t('provisioning') }}</p>
                }
                <button type="button" mat-button [attr.test-id]="'portal-manage-' + application.orgKey" (click)="store.selectApplication(application.orgKey)">
                  {{ t('applications.manage') }}
                </button>
              </mat-card-content>
            </mat-card>
          }
        </section>
      } @else if (store.loading() && !store.account()) {
        <!-- Before the prompt: while the account's applications are still being listed there is no key
             yet, and asking for one would be a question the next response answers by itself. -->
        <p test-id="portal-loading">
          <mat-progress-spinner diameter="24" mode="indeterminate" />
        </p>
      } @else if (!store.hasOrgKey() || store.notFound()) {
        <!-- No key remembered, a key that is not ours, or a portal that is switched off. All three
             lead here, and the portal does not try to tell the last two apart. -->
        <pp-organization-key-prompt />
      } @else if (store.account(); as account) {
        <mat-tab-group>
          <mat-tab [label]="t('tabs.organization')">
            <mat-card class="portal-identity" test-id="portal-identity">
              <mat-card-title>{{ account.organizationName ?? account.orgKey }}</mat-card-title>
              <mat-card-content>
                <p class="portal-org-key">{{ t('orgKey', { orgKey: account.orgKey }) }}</p>
                @if (account.applicationUrl) {
                  <a mat-raised-button color="primary" test-id="portal-application-link" [href]="account.applicationUrl">{{ t('openApplication') }}</a>
                } @else {
                  <p class="portal-note" test-id="portal-provisioning">{{ t('provisioning') }}</p>
                }
                @if (store.canSwitchApplication()) {
                  <button id="portal-switch" type="button" mat-button test-id="portal-switch" (click)="store.switchApplication()">{{ t('applications.switch') }}</button>
                } @else {
                  <button id="portal-forget" type="button" mat-button test-id="portal-forget" (click)="store.forgetOrganization()">{{ t('notMine') }}</button>
                }
              </mat-card-content>
            </mat-card>
          </mat-tab>

          <mat-tab [label]="t('tabs.subscription')">
            <pp-subscription-status-card [subscription]="store.subscription()" [plan]="store.plan()" [busy]="store.saving()" (cancel)="confirmCancel()" (resume)="resume()" />
          </mat-tab>

          <mat-tab [label]="t('tabs.invoices')">
            <pp-invoice-list [invoices]="store.invoices()" />
          </mat-tab>

          <mat-tab [label]="t('tabs.billing')">
            <mat-card class="portal-billing-party" test-id="portal-billing-party">
              <mat-card-title>{{ t('billingParty.title') }}</mat-card-title>
              <mat-card-content>
                @if (store.billingParty()) {
                  <p class="portal-note">{{ t('billingParty.lead') }}</p>
                  <pp-billing-party-form [billingParty]="store.billingParty()" (changed)="onBillingPartyChanged($event)" />
                  <button id="portal-save-billing-party" type="button" mat-raised-button color="primary" test-id="portal-save-billing-party" [disabled]="store.saving()" (click)="saveBillingParty()">
                    {{ t('billingParty.save') }}
                  </button>
                } @else {
                  <!-- A tenant bootstrapped rather than bought has nobody to invoice. Creating one from
                       here is deliberately not offered: the server refuses it, because a billing party
                       appearing without a subscription would be an invoiced party nobody sold to. -->
                  <p test-id="portal-billing-party-none">{{ t('billingParty.none') }}</p>
                }
              </mat-card-content>
            </mat-card>
          </mat-tab>
        </mat-tab-group>
      }
    </ng-container>
  `,
  styles: [
    `
      .portal-identity,
      .portal-application,
      .portal-billing-party {
        max-width: 720px;
        margin: 16px 0;
      }

      pp-subscription-status-card,
      pp-invoice-list {
        display: block;
        margin: 16px 0;
      }

      .portal-org-key {
        font-family: monospace;
        opacity: 0.75;
      }

      .portal-note {
        font-size: 0.9rem;
        opacity: 0.8;
      }
    `,
  ],
})
export class CustomerHomeComponent implements OnInit {
  readonly store = inject(CustomerAccountStore);

  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly transloco = inject(TranslocoService);

  /** The form's current value, held here because the save button is outside the form component. */
  private readonly edited = signal<Partial<BillingParty> | undefined>(undefined);

  ngOnInit(): void {
    this.store.initialise();
  }

  onBillingPartyChanged(billingParty: Partial<BillingParty>): void {
    this.edited.set(billingParty);
  }

  /**
   * Saves the edited invoiced party.
   *
   * Sent as it stands, without a client-side completeness check beyond the form's own: the server
   * validates an invoice header and answers 400 with an `errorId` the interceptor surfaces, and a
   * second definition of "valid invoice header" here would be one more place to disagree with it.
   */
  saveBillingParty(): void {
    const billingParty = this.edited();
    if (!billingParty) return;
    this.store.saveBillingParty(billingParty as BillingParty, () => this.announce('platform_subscription.portal.billingParty.saved'));
  }

  /**
   * Confirms before cancelling, and hands the dialog the period end so that it can say when service
   * actually stops. A dialog that could not name the date would have to speak vaguely about the
   * thing it most needs to be precise about.
   */
  async confirmCancel(): Promise<void> {
    const subscription = this.store.subscription();
    if (!subscription) return;
    const data: CancelSubscriptionDialogData = {
      orgKey: this.store.orgKey() ?? '',
      currentPeriodEnd: subscription.currentPeriodEnd,
    };
    const confirmed = await firstValueFrom(this.dialog.open(CancelSubscriptionDialog, { data }).afterClosed());
    if (confirmed) this.store.cancelSubscription(() => this.announce('platform_subscription.portal.cancelled'));
  }

  resume(): void {
    this.store.resumeSubscription(() => this.announce('platform_subscription.portal.resumed'));
  }

  private announce(key: string): void {
    this.snackBar.open(this.transloco.translate(key), undefined, { duration: 4000 });
  }
}
